import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/hooks/useAdminAuth';

export default function AdminLogin() {
  const { isAuthenticated, isChecking, login } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isAuthenticated) {
    navigate('/admin/dashboard', { replace: true });
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('请输入管理员邮箱');
      return;
    }
    if (!password.trim()) {
      setError('请输入管理密码');
      return;
    }
    setLoading(true);
    setError('');
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate('/admin/dashboard', { replace: true });
    } else {
      setError(result.error || '登录失败，请检查邮箱和密码');
      setPassword('');
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-background-100 px-4">
      <div className="w-full max-w-sm">
        <div className="bg-background-50 rounded-2xl border border-background-200/70 p-8">
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-primary-100 flex items-center justify-center mx-auto mb-4">
              <i className="ri-shield-check-line text-2xl text-primary-600" />
            </div>
            <h1 className="font-heading text-xl font-bold text-foreground-900 mb-1">
              管理后台
            </h1>
            <p className="text-xs text-foreground-500">请使用管理员账号登录</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="admin-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
                管理员邮箱
              </label>
              <input
                id="admin-email"
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                placeholder="admin@example.com"
                autoFocus
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="block text-xs font-medium text-foreground-700 mb-1.5">
                管理密码
              </label>
              <input
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                placeholder="请输入密码"
              />
            </div>

            {error && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <i className="ri-error-warning-line" />
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-500 hover:bg-primary-600 text-white py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors cursor-pointer inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  登录中...
                </>
              ) : (
                <>
                  <i className="ri-lock-unlock-line text-sm" />
                  登录后台
                </>
              )}
            </button>
          </form>

          <p className="text-center text-xs text-foreground-400 mt-4">
            <a href="/" className="hover:text-foreground-600 transition-colors">
              <i className="ri-arrow-left-line text-xs mr-1" />
              返回首页
            </a>
          </p>
        </div>
      </div>
    </main>
  );
}